/* eslint-disable no-shadow */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';

import PopOverMenu from '../../../../../components/PopOverMenu';
import { drawCategoryTypeIds, drawPositionTypeIds, sportTypeIds } from '../../../../../utils/eventConstants';
import { capitalize } from '../../../../../utils/formatting';
import { getAveragePickleballRatingDisplayV2 } from '../../../../../utils/events';
import { getTiebreak } from '../../../../../utils';
import {
    getMatchWinnerPositions,
    getMatchSheetTeamDisplayName,
    getMatchSheetTeamAvgDoublesUtr,
} from '../../../../../utils/matchCardUtils';
import { TBD_DISPLAY } from '../../../../../utils/courtSchedular';

import styles from './scheduleTab.scss';

const MatchSheetCard = (props) => {
    const {
        match,
        i,
        eventTimeZone,
        hidePrintButton,
        menuOptions,
        onMenuSelected,
        isOfficialHSEvent,
        isCurrentMemberAdmin,
        postScoreEnabled,
        onPostScoreClick,
        sportTypeId,
    } = props;

    const draws = useSelector(({ eventProfile }) => eventProfile.draws);

    const memberById = useMemo(
        () => new Map((match.participatingMembers ?? []).map((m) => [m.id, m])),
        [match.participatingMembers],
    );

    const [didPosition1Win, didPosition2Win] = getMatchWinnerPositions(match);
    const isTennisEvent = sportTypeId === sportTypeIds.TENNIS;

    const getMatchLocation = () => {
        const facilityAbbreviation = match.facilityAbbreviation || '';
        const courtName = match.courtName || '';
        return `${facilityAbbreviation} ${courtName}`.trim();
    };

    const getDrawName = () => {
        const draw = draws.drawsDict[match.drawId];

        if (draw.parentDrawId) {
            const categoryTypeId = draws?.drawsDict[match.drawId]?.categoryTypeId;
            let categoryType = '';
            if (categoryTypeId === drawCategoryTypeIds.BACK_DRAW) {
                categoryType = 'Backdraw';
            } else if (categoryTypeId === drawCategoryTypeIds.CONSOLATION) {
                categoryType = 'Consolation';
            } else if (categoryTypeId === drawCategoryTypeIds.THREEFOURPLAYOFF) {
                categoryType = '3-4 Playoff';
            } else if (categoryTypeId === drawCategoryTypeIds.FIVESIXPLAYOFF) {
                categoryType = '5-6 Playoff';
            }
            return `${draws.drawsDict[draw.parentDrawId]?.name} (${categoryType})`;
        }
        return draws.drawsDict[match.drawId]?.name;
    };

    const getPlayerRating = (player) => {
        return isTennisEvent ? player.singlesUtr : player.utrRange?.pkbRatingDisplay;
    };

    const getPlayerDetails = (column) => {
        if (match.position1?.typeId === drawPositionTypeIds.PLAYER) {
            if (column === 'left' && match.position1?.memberIds?.length > 0) {
                const player1 = memberById.get(match.position1.memberIds[0]);
                return player1 ? (
                    <>
                        <div className={`col-sm-12 p-0 justify-content-md-end ${didPosition1Win ? styles.winner : ''}`}>
                            {getPlayerRating(player1)} {player1.firstName} {player1.lastName}
                        </div>
                        {isOfficialHSEvent && (
                            <div className={`col-sm-12 p-0 justify-content-md-end`}>
                                {player1.highSchool}
                            </div>
                        )}
                    </>
                ) : null;
            }
        }

        if (match.position2?.typeId === drawPositionTypeIds.PLAYER) {
            if (column === 'right' && match.position2?.memberIds?.length > 0) {
                const player2 = memberById.get(match.position2.memberIds[0]);
                return player2 ? (
                    <>
                        <div className={`col-sm-12 p-0 ${didPosition2Win ? styles.winner : ''}`}>
                            {player2.firstName} {player2.lastName} {getPlayerRating(player2)}
                        </div>
                        {isOfficialHSEvent && (
                            <div className={`col-sm-12 p-0`}>
                                {player2.highSchool}
                            </div>
                        )}
                    </>
                ) : null;
            }
        }

        if (match.position1?.typeId === drawPositionTypeIds.TEAM) {
            if (column === 'left') {
                const [t1p1Id, t1p2Id] = match.position1.memberIds ?? [];
                const team1Player1 = memberById.get(t1p1Id);
                const team1Player2 = memberById.get(t1p2Id);
                const team1DisplayName = getMatchSheetTeamDisplayName(match.position1, match.participatingMembers);
                const team1Rating = isTennisEvent
                    ? getMatchSheetTeamAvgDoublesUtr(match.position1, match.participatingMembers)
                    : getAveragePickleballRatingDisplayV2(team1Player1, team1Player2);

                return (
                    <>
                        <div className={`col-sm-12 p-0 justify-content-md-end ${didPosition1Win ? styles.winner : ''}`}>
                            {team1Rating} {team1DisplayName}
                        </div>
                        {isOfficialHSEvent && (
                            <div className={`col-sm-12 p-0 justify-content-md-end`}>
                                {team1Player1?.highSchool} {team1Player2?.highSchool && team1Player2?.highSchool !== team1Player1?.highSchool ? `/ ${team1Player2.highSchool}` : ''}
                            </div>
                        )}
                    </>
                );
            }
        }

        if (match.position2?.typeId === drawPositionTypeIds.TEAM) {
            if (column === 'right') {
                const [t2p1Id, t2p2Id] = match.position2.memberIds ?? [];
                const team2Player1 = memberById.get(t2p1Id);
                const team2Player2 = memberById.get(t2p2Id);
                const team2DisplayName = getMatchSheetTeamDisplayName(match.position2, match.participatingMembers);
                const team2Rating = isTennisEvent
                    ? getMatchSheetTeamAvgDoublesUtr(match.position2, match.participatingMembers)
                    : getAveragePickleballRatingDisplayV2(team2Player1, team2Player2);

                return (
                    <>
                        <div className={`col-sm-12 p-0 ${didPosition2Win ? styles.winner : ''}`}>
                            {team2DisplayName} {team2Rating}
                        </div>
                        {isOfficialHSEvent && (
                            <div className={`col-sm-12 p-0`}>
                                {team2Player1?.highSchool} {team2Player2?.highSchool && team2Player2?.highSchool !== team2Player1?.highSchool ? `/ ${team2Player2.highSchool}` : ''}
                            </div>
                        )}
                    </>
                );
            }
        }

        return TBD_DISPLAY;
    };

    const getScores = () => {
        const walkOverValues = ['Walkover', 'Defaulted', 'walkover', 'defaulted', 'Abandoned Match', 'Double Walkover', 'abandoned match', 'double walkover'];
        const score = match.result?.score;

        if (score) {
            const scoreArray = Object.values(score);
            if (scoreArray.length > 0) {
                return (
                    <>
                        {scoreArray.map(scoreItem => {
                            if (didPosition1Win) {
                                return `${scoreItem.winner}-${scoreItem.loser}${getTiebreak(scoreItem)} `;
                            }
                            return `${scoreItem.loser}-${scoreItem.winner}${getTiebreak(scoreItem)} `;
                        })}
                        {match.result.completionType !== 'winnercompleted' && match.result.outcome && `(${capitalize(match.result.outcome)})` }
                    </>
                );
            }
            if ((walkOverValues.includes(match.result.outcome) || (match.result.completionType === 'winnerdidnotstart'))) {
                return (
                    <span className="outcome-font">{(walkOverValues.includes(match.result.outcome) && `(${capitalize(match.result.outcome)})`) || '(Walkover)'}</span>
                );
            }
        }
        if (isCurrentMemberAdmin && match.position1 && match.position2) {
            return (
                <div className="text-center">
                    <button
                        type="button"
                        className={`btn btn-default btn-sm`}
                        disabled={!postScoreEnabled}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onPostScoreClick(i);
                        }}
                        data-testid="match-sheet-card.post-score-btn"
                    >
                        Post Score
                    </button>
                </div>
            );
        }
        return '';
    };

    return (
        <div key={match.id} className={`${styles.matchCard} ${hidePrintButton ? 'border-bottom border-dark' : ''} utr-card p8 bg-white d-flex aic`}>
            <div className="row w-100 align-items-center">
                <div className={`col-md-2`}>
                    {moment.utc(`${match.date}`).tz(eventTimeZone).format('h:mm A z')}
                </div>
                <div className={`col-md-2`}>
                    {getMatchLocation()}
                </div>
                <div className={`col-md-2`}>
                    {getDrawName()}
                </div>
                <div className={`col-md-2 d-block text-right justify-content-md-end ${styles.player}`}>
                    {getPlayerDetails('left')}
                </div>
                <div className={`col-md-2 d-block ${styles.player}`}>
                    {getPlayerDetails('right')}
                </div>
                <div className={`col-md-2`}>
                    {getScores()}
                </div>
            </div>
            <div className={styles.optionsContainer}>
                {menuOptions.length > 0 && match.result && (
                    <PopOverMenu
                        horizontalEllipsis={false}
                        menuItems={menuOptions}
                        onSelected={(menuOption) => onMenuSelected(menuOption, i)}
                        anchorClass="pt4"
                    />
                )}
            </div>
        </div>
    );
};

MatchSheetCard.propTypes = {
    match: PropTypes.shape({
        id: PropTypes.string.isRequired,
        drawId: PropTypes.string.isRequired,
        isLive: PropTypes.bool,
        liveMatch: PropTypes.shape({
            status: PropTypes.string,
            gameState: PropTypes.string,
            startTime: PropTypes.string,
            matchDurationSeconds: PropTypes.number,
            periodScores: PropTypes.arrayOf(PropTypes.shape({
                home_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                away_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                homeScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                awayScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                home_tiebreak_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                away_tiebreak_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                tiebreak_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            })),
        }),
        position1: PropTypes.shape({
            typeId: PropTypes.number,
            memberIds: PropTypes.arrayOf(PropTypes.number),
        }),
        position2: PropTypes.shape({
            typeId: PropTypes.number,
            memberIds: PropTypes.arrayOf(PropTypes.number),
        }),
        participatingMembers: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            firstName: PropTypes.string,
            lastName: PropTypes.string,
            highSchool: PropTypes.string,
            singlesUtr: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            doublesUtr: PropTypes.number,
            utrRange: PropTypes.shape({
                pkbRatingDisplay: PropTypes.string,
            }),
        })),
        result: PropTypes.shape({
            score: PropTypes.objectOf(PropTypes.shape({
                winner: PropTypes.number,
                loser: PropTypes.number,
                tiebreak: PropTypes.number,
                winnerTiebreak: PropTypes.number,
            })),
            completionType: PropTypes.string,
            outcome: PropTypes.string,
        }),
        date: PropTypes.string,
        locationId: PropTypes.string,
        facilityId: PropTypes.string,
        courtId: PropTypes.string,
        courtName: PropTypes.string,
        facilityName: PropTypes.string,
        facilityAbbreviation: PropTypes.string,
    }).isRequired,
    i: PropTypes.number.isRequired,
    eventTimeZone: PropTypes.string,
    hidePrintButton: PropTypes.bool,
    menuOptions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        value: PropTypes.string,
    })),
    onMenuSelected: PropTypes.func,
    isOfficialHSEvent: PropTypes.bool,
    isCurrentMemberAdmin: PropTypes.bool,
    postScoreEnabled: PropTypes.bool,
    onPostScoreClick: PropTypes.func,
    sportTypeId: PropTypes.number,
};

MatchSheetCard.defaultProps = {
    eventTimeZone: null,
    hidePrintButton: false,
    menuOptions: [],
    onMenuSelected: () => {},
    isOfficialHSEvent: false,
    isCurrentMemberAdmin: false,
    postScoreEnabled: false,
    onPostScoreClick: () => {},
    sportTypeId: null,
};

export default MatchSheetCard;
